import React from 'react';
import { VenueType } from '../../../features/landing/constants/venueTypes';
import type { VenueTypeCode } from '../../../features/landing/constants/venueTypes';
import { KidsLoadingAnimation } from './KidsLoadingAnimation';
import { SnookerLoadingAnimation } from './SnookerLoadingAnimation';
import { GamingLoadingAnimation } from './GamingLoadingAnimation';

interface ThemedLoadingAnimationProps {
  venueType?: VenueTypeCode;
  message?: string;
}

/** Rotating message pools per venue type. */
const MESSAGE_POOLS: Record<VenueTypeCode | 'DEFAULT', readonly string[]> = {
  [VenueType.KIDS_PLAYAREA]: [
    'Getting the fun ready...',
    'Almost playtime!',
    'Building your adventure...',
    'Setting up the play zone...',
  ],
  [VenueType.SNOOKER]: [
    'Setting up the table...',
    'Chalking up...',
    'Racking the reds...',
    'Spotting the black...',
  ],
  [VenueType.GAMING]: [
    'Loading your game...',
    'Powering up...',
    'Entering the arena...',
    'Booting the system...',
  ],
  DEFAULT: ['Loading...', 'Almost there...', 'Just a moment...', 'Getting things ready...'],
};

function pickMessage(pool: readonly string[]): string {
  return pool[Math.floor(Math.random() * pool.length)];
}

/**
 * Renders a venue-themed loading animation.
 * Falls back to a random theme when no venue type is specified.
 * When no message is provided, a rotating message is picked from the
 * venue-type-specific message pool on each mount.
 */
const ThemedLoadingAnimationBase: React.FC<ThemedLoadingAnimationProps> = ({
  venueType,
  message,
}) => {
  // Pick a stable random theme if none specified (stable per mount)
  const resolvedType = React.useMemo(() => {
    if (venueType) return venueType;
    const types = [VenueType.GAMING, VenueType.SNOOKER, VenueType.KIDS_PLAYAREA] as const;
    return types[Math.floor(Math.random() * types.length)];
  }, [venueType]);

  // Pick a stable rotating message if none provided (stable per mount)
  const resolvedMessage = React.useMemo(() => {
    if (message !== undefined) return message;
    const pool = MESSAGE_POOLS[resolvedType] ?? MESSAGE_POOLS.DEFAULT;
    return pickMessage(pool);
  }, [message, resolvedType]);

  switch (resolvedType) {
    case VenueType.KIDS_PLAYAREA:
      return <KidsLoadingAnimation message={resolvedMessage} />;
    case VenueType.SNOOKER:
      return <SnookerLoadingAnimation message={resolvedMessage} />;
    case VenueType.GAMING:
    default:
      return <GamingLoadingAnimation message={resolvedMessage} />;
  }
};

export const ThemedLoadingAnimation = React.memo(ThemedLoadingAnimationBase);
